import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Video, Save, ExternalLink, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth, API } from "../App";

/** Lets instructors paste their Zoom Personal Meeting Room URL.
 *  Students see this link as a "Join Live Class" button on their dashboard,
 *  the welcome packet, and their cohort page. The button only activates
 *  during scheduled class hours. */
const InstructorZoomCard = () => {
  const { user, checkAuth } = useAuth();
  const [link, setLink] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setLink(user.zoom_personal_link || "");
  }, [user]);

  // Only show this card to instructors and admins
  if (!user || (user.role !== "instructor" && user.role !== "admin")) return null;

  const isValid = (s) => {
    const v = (s || "").trim().toLowerCase();
    if (v === "") return true; // empty is allowed (clears link)
    return v.startsWith("https://") && v.includes("zoom.us/");
  };

  const handleSave = async () => {
    if (!isValid(link)) {
      toast.error("Link must look like https://...zoom.us/...");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/users/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ zoom_personal_link: link.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Save failed");
      }
      toast.success(link.trim() ? "Zoom link saved." : "Zoom link cleared.");
      checkAuth();
    } catch (e) {
      toast.error(e.message || "Could not save your Zoom link");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setLink("");
  };

  const saved = (user.zoom_personal_link || "").trim();
  const dirty = (link || "").trim() !== saved;

  return (
    <Card className="card-surface" data-testid="instructor-zoom-card">
      <CardHeader>
        <CardTitle className="text-slate-900 flex items-center gap-2">
          <Video className="w-5 h-5 text-brand-navy" />
          Live Classroom — Zoom Personal Link
        </CardTitle>
        <p className="text-slate-500 text-sm">
          Paste your Zoom Personal Meeting Room link. Students in your cohorts
          will see a <strong>Join Live Class</strong> button that activates during scheduled
          class hours (Mon–Wed, 9 AM – 12 PM CT).
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="zoom-link" className="text-slate-600">Zoom Personal Meeting URL</Label>
            <div className="flex gap-2">
              <Input
                id="zoom-link"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="https://us05web.zoom.us/j/1234567890?pwd=…"
                className="input-base"
                data-testid="input-zoom-link"
              />
              {link && (
                <Button
                  type="button"
                  onClick={handleClear}
                  variant="outline"
                  className="shrink-0"
                  data-testid="clear-zoom-link-btn"
                  aria-label="Clear"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            {!isValid(link) && (
              <p className="text-xs text-rose-600">Link must start with <code>https://</code> and include <code>zoom.us/</code></p>
            )}
          </div>

          {saved && (
            <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
              <div className="flex items-center gap-2 text-sm text-emerald-800">
                <CheckCircle2 className="w-4 h-4" />
                <span>Saved & visible to your students</span>
              </div>
              <a
                href={saved}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-brand-navy hover:underline"
                data-testid="test-zoom-link"
              >
                Open <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              type="button"
              onClick={handleSave}
              disabled={saving || !dirty || !isValid(link)}
              className="btn-primary flex items-center gap-2"
              data-testid="save-zoom-link-btn"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Zoom Link
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default InstructorZoomCard;
